import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { ArrowDown } from '@ankr.com/ui';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { makeStyles } from 'tss-react/mui';

import { cropString, getWalletIcon, useChainData, ZERO } from 'modules/common';
import {
  useAppDispatch,
  useAppSelector,
  useGetAccountAddressQuery,
  useGetAccountBalanceQuery,
  useGetNativeTokenUSDPriceQuery,
} from 'modules/store';
import { setShowLowBalancePopup } from 'modules/store/uiSlice';

import { useIsLowBalancePopupQuery } from '../actions/isLowBalancePopup';
import { Balance } from './Balance';
import { DisconnectDropdown } from './DisconnectDropdown/DisconnectDropdown';
import { LowBalancePopup } from './LowBalancePopup/LowBalancePopup';
import { NetworkMenu } from './NetworkMenu';

interface Props {
  address: string;
}

export function Account({ address }: Props): JSX.Element {
  const [isLogoutDropdownOpen, setIsLogoutDropdownOpen] = useState(false);
  const [isNetworkMenu, setIsNetworkMenu] = useState(false);
  const { classes, cx } = useStyles();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const chainData = useChainData();
  const networkIcon = chainData?.icon.default || '';
  const networkName = chainData?.name || '';
  const { data: accountAddress = '' } = useGetAccountAddressQuery();
  const { data: accountBalance = ZERO } = useGetAccountBalanceQuery({
    accountAddress,
  });
  const { showLowBalancePopup } = useAppSelector(state => state.ui);
  const dispatch = useAppDispatch();
  const { data: USDPrice } = useGetNativeTokenUSDPriceQuery({
    chainId: chainData?.chainId || null,
  });
  const USDAccountBalance = USDPrice
    ? accountBalance.multipliedBy(USDPrice)
    : null;

  const networkRef = useRef(null);
  const rootRef = useRef(null);

  const walletId = useAppSelector(store => store.auth.walletId);
  const walletIcon = walletId && getWalletIcon(walletId);
  const { data: isLowBalance = false } = useIsLowBalancePopupQuery({
    accountAddress,
    USDAccountBalance,
  });

  useEffect(() => {
    if (!isLowBalance) {
      return;
    }

    if (showLowBalancePopup === null && isLowBalance) {
      dispatch(setShowLowBalancePopup(true));
    }
  }, [showLowBalancePopup, dispatch, USDAccountBalance, isLowBalance]);

  return (
    <div
      data-tid="layout-header-account"
      className={classes.account}
      ref={rootRef}
    >
      <Balance balance={accountBalance} isLow={isLowBalance} />

      {!isMobile && (
        <div
          data-tid="layout-header-account-network-menu-button"
          className={classes.tag}
          ref={networkRef}
          onClick={() => setIsNetworkMenu(true)}
        >
          {networkIcon && (
            <Box
              component="img"
              src={networkIcon}
              alt={networkName}
              width={24}
            />
          )}

          <Typography
            className={classes.tagText}
            variant="body4"
            sx={theme => ({
              color: theme.palette.grey[900],
            })}
          >
            {networkName}
          </Typography>

          <ArrowDown
            sx={{ ml: 1 }}
            className={cx(classes.arrow, isNetworkMenu && classes.arrowRotated)}
          />
        </div>
      )}

      <div
        data-tid="layout-header-account-dropdown-menu-button"
        className={classes.tag}
        onClick={() => setIsLogoutDropdownOpen(true)}
      >
        {walletIcon && (
          <Box component="img" src={walletIcon} alt={networkName} width={24} />
        )}

        <Typography
          className={classes.tagText}
          variant="body4"
          sx={theme => ({
            color: theme.palette.grey[900],
          })}
        >
          {cropString(address)}
        </Typography>

        <ArrowDown
          sx={{ ml: 1 }}
          className={cx(
            classes.arrow,
            isLogoutDropdownOpen && classes.arrowRotated,
          )}
        />
      </div>

      <NetworkMenu
        open={isNetworkMenu}
        anchorEl={networkRef.current}
        onClose={() => setIsNetworkMenu(false)}
        className={classes.dropdown}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      />

      <DisconnectDropdown
        address={address}
        open={isLogoutDropdownOpen}
        anchorEl={rootRef.current}
        onClose={() => setIsLogoutDropdownOpen(false)}
        className={classes.dropdown}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      />

      <LowBalancePopup
        className={classes.lowFundsPopup}
        open={!!showLowBalancePopup}
        anchorEl={networkRef.current}
        onClose={() => dispatch(setShowLowBalancePopup(false))}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      />
    </div>
  );
}

const useStyles = makeStyles()(theme => ({
  account: {
    display: 'flex',
    gap: theme.spacing(2),
    position: 'relative',
    cursor: 'pointer',
  },

  tag: {
    display: 'flex',
    alignItems: 'center',
    height: 40,
    borderRadius: 12,
    padding: theme.spacing(2, 4),
    backgroundColor: theme.palette.common.white,

    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2.5, 2),
    },
  },

  tagText: {
    '&&': {
      display: 'inline-block',

      '&:not(:first-child)': {
        marginLeft: theme.spacing(2),
      },

      [theme.breakpoints.down('sm')]: {
        display: 'none',
      },
    },
  },

  lowFundsPopup: {
    marginTop: theme.spacing(5),
    marginLeft: theme.spacing(-6),
  },

  dropdown: {
    marginTop: theme.spacing(2),
  },

  arrow: {
    '&&': {
      transition: 'all .1s ease',
    },
  },

  arrowRotated: {
    transform: 'rotate(-180deg)',
  },
}));
